import {
    formatCurrencyInputOnChange,
    formatDollars,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import {
    type MembershipProfileCashFlowAnswer,
    MembershipProfileCategory,
    QuestionKey,
} from '@fruitful/member-profile';
import React, { useMemo, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    CheckIcon,
    EditIcon,
    Icon,
    IconButton,
    Input,
    RotateCcwIcon,
    SaveIcon,
    Spinner,
    useToastController,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import type { MembershipDetails } from '../../entities/entityTypes';
import useUpsertMembershipProfileAnswer from '../hooks/useUpsertMembershipProfileAnswer';

export const MembershipSavingsInfo = ({
    membershipDetails,
}: {
    membershipDetails: MembershipDetails;
}) => {
    const toast = useToastController();

    const answers = membershipDetails.membershipProfileAnswers;
    const pretaxAnswer = answers.find(
        (answer) =>
            answer.membershipProfileQuestion.questionKey ===
                QuestionKey.SAVINGS_PRE_TAX &&
            answer.membershipProfileQuestion.category ===
                MembershipProfileCategory.CASH_FLOW,
    );
    const posttaxAnswer = answers.find(
        (answer) =>
            answer.membershipProfileQuestion.questionKey ===
                QuestionKey.SAVINGS_POST_TAX &&
            answer.membershipProfileQuestion.category ===
                MembershipProfileCategory.CASH_FLOW,
    );
    const savingsAnswer = answers.find(
        (answer) =>
            answer.membershipProfileQuestion.questionKey ===
                QuestionKey.SAVINGS &&
            answer.membershipProfileQuestion.category ===
                MembershipProfileCategory.CASH_FLOW,
    );

    const [isEditing, setIsEditing] = useState(false);

    const [pretaxValue, setPretaxValue] = useState(
        (pretaxAnswer?.answer as MembershipProfileCashFlowAnswer)?.answer ?? '',
    );
    const [posttaxValue, setPosttaxValue] = useState(
        (posttaxAnswer?.answer as MembershipProfileCashFlowAnswer)?.answer ??
            '',
    );
    const [savingsValue, setSavingsValue] = useState(
        formatDollars(
            ((savingsAnswer?.answer as MembershipProfileCashFlowAnswer)
                ?.answer as unknown as number) ?? 0,
            false,
        ),
    );

    const [isLoading, setIsLoading] = useState(false);
    const { mutateAsync: upsertMembershipProfileAnswer } =
        useUpsertMembershipProfileAnswer();

    const toggleEditing = () => {
        setIsEditing((prev) => !prev);
    };

    const saveLegacySavings = async (
        pretaxAnswer: string,
        postTaxAnswer: string,
    ) => {
        const pretaxRequestBody = {
            membershipId: membershipDetails.id,
            answeredByUserId: membershipDetails.users[0]?.id ?? '',
            questionKey: QuestionKey.SAVINGS_PRE_TAX,
            answer: {
                answer: pretaxAnswer,
                additionalInformation: null,
            },
        };

        const postTaxRequestBody = {
            membershipId: membershipDetails.id,
            answeredByUserId: membershipDetails.users[0]?.id ?? '',
            questionKey: QuestionKey.SAVINGS_POST_TAX,
            answer: {
                answer: postTaxAnswer,
                additionalInformation: null,
            },
        };

        try {
            await upsertMembershipProfileAnswer({
                body: [pretaxRequestBody, postTaxRequestBody],
                urlParams: { membershipId: membershipDetails.id },
            });
            toast.show('Savings Contributions updated', {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$green1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={CheckIcon} size={24} color="$white" />
                    </View>
                ),
            });
            setIsLoading(false);
            setIsEditing((prev) => !prev);
        } catch (e) {
            toast.show(
                'Error updating savings contributions. Please try again.',
                {
                    icon: (
                        <View
                            alignItems="center"
                            justifyContent="center"
                            backgroundColor="$red1000"
                            borderRadius={12}
                            padding={9}
                        >
                            <Icon icon={XIcon} size={24} color="$white" />
                        </View>
                    ),
                },
            );
            setIsLoading(false);
        }
    };

    const saveSavings = async (savingsValue: string) => {
        const finalValue = Math.round(
            formattedDollarsToCents(savingsValue) / 100,
        );

        const requestBody = {
            membershipId: membershipDetails.id,
            answeredByUserId: membershipDetails.users[0]?.id ?? '',
            questionKey: QuestionKey.SAVINGS,
            answer: {
                answer: finalValue,
            },
        };

        try {
            await upsertMembershipProfileAnswer({
                body: [requestBody],
                urlParams: { membershipId: membershipDetails.id },
            });
            toast.show('Savings updated', {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$green1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={CheckIcon} size={24} color="$white" />
                    </View>
                ),
            });
            setIsLoading(false);
            setIsEditing((prev) => !prev);
        } catch (e) {
            toast.show('Error updating savings. Please try again.', {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$red1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={XIcon} size={24} color="$white" />
                    </View>
                ),
            });
            setIsLoading(false);
        }
    };

    const noDataYet = useMemo(() => {
        return (
            pretaxAnswer == null &&
            posttaxAnswer == null &&
            savingsAnswer == null
        );
    }, [pretaxAnswer, posttaxAnswer, savingsAnswer]);

    return (
        <YStack
            borderRadius="$4"
            backgroundColor="$white"
            padding="$5"
            gap="$4"
            shadowColor="$marigold800"
            shadowOffset={{ width: 0, height: 1 }}
            shadowOpacity={0.5}
            shadowRadius={4}
        >
            <YStack
                borderBottomWidth={1}
                borderColor="$gray200"
                paddingBottom="$2"
                marginBottom="$4"
            >
                <XStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <BodyLargeEmphasized>Savings</BodyLargeEmphasized>
                    {isLoading ? (
                        <Spinner />
                    ) : isEditing ? (
                        <XStack gap={8}>
                            <IconButton
                                variant="secondary"
                                icon={SaveIcon}
                                onPress={() => {
                                    setIsLoading(true);
                                    if (pretaxAnswer ?? posttaxAnswer) {
                                        void saveLegacySavings(
                                            pretaxValue,
                                            posttaxValue,
                                        );
                                    } else {
                                        void saveSavings(savingsValue);
                                    }
                                }}
                                disabled={isLoading}
                            />
                            <IconButton
                                variant="secondary"
                                icon={RotateCcwIcon}
                                onPress={() => {
                                    setIsLoading(false);
                                    setIsEditing(false);
                                    setPosttaxValue(
                                        (
                                            posttaxAnswer?.answer as MembershipProfileCashFlowAnswer
                                        )?.answer ?? '',
                                    );
                                    setPretaxValue(
                                        (
                                            pretaxAnswer?.answer as MembershipProfileCashFlowAnswer
                                        )?.answer ?? '',
                                    );
                                    setSavingsValue(
                                        formatDollars(
                                            ((
                                                savingsAnswer?.answer as MembershipProfileCashFlowAnswer
                                            )?.answer as unknown as number) ??
                                                0,
                                            false,
                                        ),
                                    );
                                }}
                                disabled={isLoading}
                            />
                        </XStack>
                    ) : (
                        <IconButton
                            variant="secondary"
                            icon={EditIcon}
                            onPress={toggleEditing}
                            disabled={isLoading || noDataYet}
                        />
                    )}
                </XStack>
            </YStack>
            <YStack>
                {noDataYet ? (
                    <BodyMediumRegular>No data yet</BodyMediumRegular>
                ) : (
                    <XStack
                        width="100%"
                        justifyContent="space-between"
                        gap="$4"
                    >
                        {savingsAnswer ? (
                            isEditing ? (
                                <Input
                                    value={savingsValue}
                                    onChangeText={(text: string) => {
                                        const newAmount =
                                            formatCurrencyInputOnChange(
                                                savingsValue ?? '$',
                                                text,
                                            ).replace(' ', '');
                                        setSavingsValue(newAmount);
                                    }}
                                    keyboardType="numeric"
                                    width={120}
                                    fontSize={16}
                                    disabled={isLoading}
                                />
                            ) : (
                                <BodyLargeEmphasized>
                                    {savingsValue}
                                </BodyLargeEmphasized>
                            )
                        ) : (
                            <>
                                <YStack flex={1} gap={8}>
                                    <BodyMediumEmphasized>
                                        Pre-tax savings
                                    </BodyMediumEmphasized>
                                    <YStack
                                        borderWidth={1}
                                        borderColor="$gray300"
                                        borderRadius={8}
                                        backgroundColor="$white"
                                        paddingHorizontal="$3"
                                        paddingVertical="$2"
                                        minHeight={40}
                                        justifyContent="center"
                                    >
                                        {isEditing ? (
                                            <Input
                                                value={pretaxValue}
                                                onChangeText={setPretaxValue}
                                                placeholder="Enter pre-tax savings"
                                            />
                                        ) : (
                                            <BodyLargeEmphasized color="$gray900">
                                                {pretaxValue || '—'}
                                            </BodyLargeEmphasized>
                                        )}
                                    </YStack>
                                </YStack>

                                <YStack flex={1} gap={8}>
                                    <BodyMediumEmphasized>
                                        Post-tax savings
                                    </BodyMediumEmphasized>
                                    <YStack
                                        borderWidth={1}
                                        borderColor="$gray300"
                                        borderRadius={8}
                                        backgroundColor="$white"
                                        paddingHorizontal="$3"
                                        paddingVertical="$2"
                                        minHeight={40}
                                        justifyContent="center"
                                    >
                                        {isEditing ? (
                                            <Input
                                                value={posttaxValue}
                                                onChangeText={setPosttaxValue}
                                                placeholder="Enter post-tax savings"
                                            />
                                        ) : (
                                            <BodyLargeEmphasized color="$gray900">
                                                {posttaxValue || '—'}
                                            </BodyLargeEmphasized>
                                        )}
                                    </YStack>
                                </YStack>
                            </>
                        )}
                    </XStack>
                )}
            </YStack>
        </YStack>
    );
};
